import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Category } from "@/lib/types";
import { getTranslatedText } from "@/lib/translations";

interface Props {
  /** Catégories affichées, dans l'ordre des sections (`section-<id>`) rendues en `children`. */
  categories: Category[];
  children: ReactNode;
}

// Une section devient active quand son haut passe sous la colonne (collée sous l'en-tête), à ce jeu près.
const ACTIVE_OFFSET = 8;
// Après un appui, la sélection est figée jusqu'à la fin du défilement animé (plus d'événement `scroll`
// pendant ce délai), sinon les sections traversées s'allumeraient tour à tour.
const SCROLL_END_DELAY = 150;
// Repli si l'appui ne fait pas défiler (section déjà en place).
const TAP_LOCK_MAX = 1000;

const sectionEl = (id: string): HTMLElement | null => document.getElementById(`section-${id}`);

/**
 * Affichage « catégories à gauche » (`settings.smartphoneCategoryLayout = "sidebar"`) : colonne de
 * catégories collée sous l'en-tête, qui défile seule, et les sections de la carte à droite. Comme la
 * tablette en paysage (CategoryBar vertical) : un appui fait défiler jusqu'à la section, la catégorie
 * active suit le défilement et reste visible dans la colonne.
 */
export const CategorySidebar = ({ categories, children }: Props): JSX.Element => {
  const { t, i18n } = useTranslation();
  const railRef = useRef<HTMLElement | null>(null);
  const [activeId, setActiveId] = useState<string | null>(categories[0]?.id ?? null);
  const locked = useRef(false);
  const unlockTimer = useRef<number | undefined>(undefined);

  const lockFor = useCallback((ms: number): void => {
    locked.current = true;
    window.clearTimeout(unlockTimer.current);
    unlockTimer.current = window.setTimeout(() => {
      locked.current = false;
    }, ms);
  }, []);

  // Dernière section dont le haut a passé la colonne ; tout en bas de page, la dernière (une courte
  // section finale n'atteint jamais le haut).
  const syncActive = useCallback((): void => {
    const rail = railRef.current;
    if (!rail || !categories.length) return;
    const doc = document.documentElement;
    if (window.scrollY > 0 && window.innerHeight + window.scrollY >= doc.scrollHeight - 2) {
      setActiveId(categories[categories.length - 1].id);
      return;
    }
    const line = rail.getBoundingClientRect().top + ACTIVE_OFFSET;
    let current = categories[0].id;
    for (const category of categories) {
      const el = sectionEl(category.id);
      if (!el || el.getBoundingClientRect().top > line) break;
      current = category.id;
    }
    setActiveId(current);
  }, [categories]);

  useEffect(() => {
    syncActive();
    const onScroll = (): void => {
      if (locked.current) {
        lockFor(SCROLL_END_DELAY);
        return;
      }
      syncActive();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", syncActive);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", syncActive);
    };
  }, [syncActive, lockFor]);

  useEffect(() => () => window.clearTimeout(unlockTimer.current), []);

  // Garde la catégorie active au milieu de la colonne (sans faire défiler la page).
  useEffect(() => {
    const rail = railRef.current;
    const button = activeId ? rail?.querySelector<HTMLElement>(`[data-category="${activeId}"]`) : null;
    if (!rail || !button) return;
    const top = button.offsetTop - (rail.clientHeight - button.offsetHeight) / 2;
    rail.scrollTo?.({ top: Math.max(0, top), behavior: "smooth" });
  }, [activeId]);

  const onSelect = (categoryId: string): void => {
    setActiveId(categoryId);
    lockFor(TAP_LOCK_MAX);
    sectionEl(categoryId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="flex items-start">
      <nav
        ref={railRef}
        aria-label={t("categoryPicker.title", "Catégories")}
        className="sticky w-[88px] shrink-0 overflow-y-auto overscroll-contain border-r border-border bg-surface/70"
        style={{ top: "var(--header-h, 88px)", height: "calc(100dvh - var(--header-h, 88px))" }}
      >
        <ul className="py-2 pb-safe">
          {categories.map((category) => {
            const active = category.id === activeId;
            return (
              <li key={category.id}>
                <button
                  type="button"
                  data-category={category.id}
                  aria-current={active ? "true" : undefined}
                  onClick={() => onSelect(category.id)}
                  className={`relative w-full px-2.5 py-3.5 text-left text-[13px] leading-snug break-words hyphens-auto transition-colors ${
                    active ? "bg-bg font-semibold text-primary" : "text-ink-soft hover:text-ink"
                  }`}
                >
                  {active && (
                    <span className="absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-r-full bg-primary" aria-hidden="true" />
                  )}
                  {getTranslatedText(category, "name", i18n.language)}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
      <div className="min-w-0 flex-1 pl-2.5 pr-3 pb-32 space-y-8">{children}</div>
    </div>
  );
};
